// deps
import { useCallback, useContext, useEffect, useMemo, useState } from "react";

// internal-deps
import { GroupOrderAnalyticsPayload } from "@swiggy-private/cart-types";
import {
    AnalyticsContext,
    GlobalHeaderState,
    SwgyHeaderKeysList,
    GroupOrderCartContext,
} from "@swiggy-private/checkout-core";
import { isEmpty } from "@swiggy-private/js-utils";

// analytics
import {
    clickGroupCartCheckoutcta,
    clickGroupCartItem,
    impressionGroupCartCheckoutcta,
    impressionGroupCartItem,
    impressionGroupCartPage,
} from "@cart-client/analytics/events";

export interface GroupOrderAnalyticsProps {
    impressionGrpCartItem: (type: string, position: number, userId: string, itemIds?: string[]) => void;
    clickGrpCartItemEvent: (label: string) => void;
    impressionCheckoutCta: VoidFunction;
    clickCheckoutCtaEvent: VoidFunction;
}

const useGroupCartAnalytics = (groupCartId: string): GroupOrderAnalyticsProps => {
    const { state } = useContext(GroupOrderCartContext);
    const { data: groupCartData } = state;
    const [firstLoad, setFirstLoad] = useState(true);
    const { clickEvent, impressionEvent, screenViewEvent } = useContext(AnalyticsContext);

    const currentUserId = GlobalHeaderState.getHeader(SwgyHeaderKeysList.userId);

    const hostUserId = useMemo(() => {
        return groupCartData?.host_user_id;
    }, [groupCartData?.host_user_id]);

    const isHost = useMemo(() => currentUserId === hostUserId, [currentUserId, hostUserId]);

    const analyticsPayload: GroupOrderAnalyticsPayload = useMemo(
        () => ({
            groupCartId,
            isInitiator: isHost,
            restId: groupCartData?.restaurant_details?.restaurant_id || "",
            userId: currentUserId,
        }),
        [isHost, currentUserId, groupCartData?.restaurant_details?.restaurant_id, groupCartId],
    );

    useEffect(() => {
        if (!isEmpty(analyticsPayload.restId) && firstLoad) {
            impressionGroupCartPage(screenViewEvent, analyticsPayload);
            setFirstLoad(false);
        }
    }, [analyticsPayload, firstLoad, groupCartId, screenViewEvent, setFirstLoad]);

    const impressionGrpCartItem = useCallback(
        (type: string, position: number, userId: string, itemIds?: string[]) => {
            impressionGroupCartItem(impressionEvent, {
                ...analyticsPayload,
                type,
                position,
                userId,
                itemIds,
            });
        },
        [analyticsPayload, impressionEvent],
    );

    const clickGrpCartItemEvent = useCallback(
        (label: string) => {
            clickGroupCartItem(clickEvent, label, analyticsPayload);
        },
        [analyticsPayload, clickEvent],
    );

    const impressionCheckoutCta = useCallback(() => {
        impressionGroupCartCheckoutcta(impressionEvent, analyticsPayload);
    }, [analyticsPayload, impressionEvent]);

    const clickCheckoutCtaEvent = useCallback(() => {
        clickGroupCartCheckoutcta(clickEvent, analyticsPayload);
    }, [analyticsPayload, clickEvent]);

    return {
        impressionGrpCartItem,
        clickGrpCartItemEvent,
        impressionCheckoutCta,
        clickCheckoutCtaEvent,
    };
};

export default useGroupCartAnalytics;
